import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";
import { MaskedLines } from "./motion";
import { IMAGES } from "../assets";

export const Hero = () => {
  const { t } = useLang();
  const { openClient, openPro } = useFlow();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="hero" ref={ref} className="relative min-h-[100vh] flex flex-col pt-32 pb-10 overflow-hidden" data-testid="hero">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-cream via-white to-cream" />
      <div className="absolute top-24 -left-24 w-[420px] h-[420px] rounded-full bg-sage-light/50 blur-[120px] -z-10" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 w-full flex-1 flex flex-col">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="inline-flex items-center gap-2 self-start rounded-full border border-line bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.15em] uppercase text-forest" data-testid="hero-eyebrow">
          <HeartHandshake size={14} /> {t.hero.eyebrow}
        </motion.div>

        <h1 className="mt-8 font-heading text-[13vw] leading-[0.98] sm:text-6xl md:text-7xl lg:text-[5.5rem] font-light tracking-tighter text-ink max-w-5xl">
          <MaskedLines lines={t.hero.titleLines} accent={t.hero.accentWord} delay={0.3} />
        </h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="max-w-xl text-lg text-ink-soft leading-relaxed" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="flex flex-col items-start gap-3">
            <div className="flex flex-wrap gap-3">
              <button onClick={() => openClient()} className="group inline-flex items-center gap-2 rounded-full bg-forest text-white font-semibold px-7 py-4 hover:bg-sage-deep transition-all duration-300 hover:-translate-y-1 shadow-soft" data-testid="hero-cta-help">
                {t.hero.ctaPrimary}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button onClick={openPro} className="inline-flex items-center rounded-full bg-petrol text-white font-semibold px-7 py-4 hover:opacity-90 transition-all duration-300 hover:-translate-y-1" data-testid="hero-cta-pro">
                {t.hero.ctaSecondary}
              </button>
            </div>
            <span className="text-sm text-ink-faint pl-1">{t.hero.note}</span>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="max-w-7xl mx-auto px-6 md:px-10 w-full mt-14">
        <div className="relative rounded-[2rem] overflow-hidden h-[40vh] md:h-[58vh] shadow-[0_24px_50px_rgb(3,33,42,0.12)]">
          <motion.img src={IMAGES.hero} alt="Hogar moderno y luminoso con una persona de confianza ayudando" style={{ y, scale }} className="w-full h-full object-cover" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-sage-deep/30 to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};
